import React, { useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { trackEvent, identifyNewUser } from "../utils/posthog";
import DynamicTitle from "./DynamicTitle";

function SignInSignUp() {
  const { login } = useAuth();
  const location = useLocation();
  // Open directly in sign-up mode when sent here from a "Sign up free" / free-tier CTA.
  const [isSignUp, setIsSignUp] = useState(location.state?.mode === "register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const navigate = useNavigate();

  const toggleForm = () => {
    setIsSignUp(!isSignUp);
    // Clear form fields and errors when toggling
    setEmail("");
    setPassword("");
    setFirstName("");
    setLastName("");
    setEmailError("");
    setPasswordError("");
    setFirstNameError("");
    setLastNameError("");
    setSuccessMessage("");
  };

  const validateEmail = (emailValue) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(emailValue);
  };

  const validatePassword = (pwd) => {
    const passwordPattern =
      /^(?=.*[0-9])(?=.*[!@#$%^&*]).{8,}$/;
    console.log("This password is " + passwordPattern.test(pwd));
    return passwordPattern.test(pwd);
    // return true
  };

  const validateName = (name) => {
    return name.trim().length > 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const apiUrl = isSignUp ? "/api/register" : "/api/login";
    console.log(`Sending request to: ${apiUrl}`);

    // Validate email
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    } else {
      setEmailError("");
    }

    // Validate password
    if (!validatePassword(password)) {
      setPasswordError(
        "Password must be at least 8 characters long and include a number and a symbol.",
      );
      return;
    } else {
      setPasswordError("");
    }

    // Validate first name and last name for sign up
    if (isSignUp) {
      if (!validateName(firstName)) {
        setFirstNameError("Please enter your first name.");
        return;
      } else {
        setFirstNameError("");
      }

      if (!validateName(lastName)) {
        setLastNameError("Please enter your last name.");
        return;
      } else {
        setLastNameError("");
      }
    }

    try {
      if (isSignUp) {
        const response = await axios({
          method: "post",
          url: apiUrl,
          data: { email, password, first_name: firstName, last_name: lastName },
          headers: {
            "Content-Type": "application/json",
          },
        });

        identifyNewUser(email, { signup_source: "email_password" });
        trackEvent("user_signed_up", {
          login_type: "email",
        });

        console.log(response.data);
        // Auto-login so a new free account lands straight in the app — no
        // second form. Falls back to the sign-in view if login hiccups.
        try {
          await login(email, password);
          trackEvent("user_logged_in", { login_type: "email", source: "post_signup" });
          navigate("/sessions");
        } catch (loginErr) {
          console.error("Auto-login after signup failed:", loginErr);
          setSuccessMessage("Account created. Please sign in to continue.");
          setIsSignUp(false);
        }
      } else {
        // Use the login function from AuthContext for sign-in
        // identifyUser is called automatically in AuthContext.fetchUser()
        await login(email, password);
        trackEvent("user_logged_in", { login_type: "email" });
        navigate("/sessions");
      }
    } catch (error) {
      console.error(isSignUp ? "Registration failed:" : "Login failed:", error);
      toast.error(error.response?.data?.error || error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <DynamicTitle>Welcome</DynamicTitle>
      <Header />
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Welcome Section */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-text mb-2">
              {isSignUp ? "Welcome to RealignMind" : "Welcome Back"}
            </h1>
            <p className="text-textSecondary">
              {isSignUp 
                ? "Join our community and start your mental wellness journey" 
                : "Sign in to continue your wellness journey"
              }
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white py-8 px-6 rounded-lg border border-tint">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-semibold text-text">
                {isSignUp ? "Create Your Account" : "Sign In"}
              </h2>
            </div>
            
            {successMessage && (
              <div className="bg-statusOk/10 border border-statusOk/30 text-statusOk px-4 py-3 rounded-md mb-4">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-text mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                />
                {emailError && (
                  <p className="mt-1 text-sm text-statusError">{emailError}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-text mb-1"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                />
                {passwordError && (
                  <p className="mt-1 text-sm text-statusError">{passwordError}</p>
                )}
              </div>

              {isSignUp && (
                <>
                  <div>
                    <label
                      htmlFor="firstName"
                      className="block text-sm font-medium text-text mb-1"
                    >
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      placeholder="Enter your first name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                    />
                    {firstNameError && (
                      <p className="mt-1 text-sm text-statusError">{firstNameError}</p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-sm font-medium text-text mb-1"
                    >
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="mt-1 p-3 block w-full border border-tint rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/30 transition-colors"
                    />
                    {lastNameError && (
                      <p className="mt-1 text-sm text-statusError">{lastNameError}</p>
                    )}
                  </div>
                </>
              )}

              <button
                type="submit"
                className="w-full bg-accent hover:bg-accentDark text-white font-semibold py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2"
              >
                {isSignUp ? "Create Account" : "Sign In"}
              </button>
            </form>

            {/* Links Section */}
            <div className="mt-6 space-y-3 text-center">
              <div>
                {isSignUp ? (
                  <p className="text-sm text-textSecondary">
                    Already have an account?{" "}
                    <button
                      onClick={toggleForm}
                      className="text-accent hover:underline font-medium transition-colors"
                    >
                      Sign in here
                    </button>
                  </p>
                ) : (
                  <p className="text-sm text-textSecondary">
                    Don't have an account?{" "}
                    <button
                      onClick={toggleForm}
                      className="text-accent hover:underline font-medium transition-colors"
                    >
                      Create one here
                    </button>
                  </p>
                )}
              </div>
              <div>
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="text-sm text-textSecondary hover:text-accent transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="text-center">
            <p className="text-xs text-textSecondary">
              Secure • Private • Research-backed
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default SignInSignUp;
